import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { CalendarDays, Clock, Heart, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = [CalendarDays, Clock, Heart, MapPin];

const DEFAULT_STATS = [
  { value: "5", suffix: "", label: "Days a week, Monday to Friday" },
  { value: "10–3:30", suffix: "", label: "Our daily hours" },
  { value: "100", suffix: "%", label: "Person-centred days" },
  { value: "Salisbury", suffix: "", label: "Where we're based" },
];

const CountValue = ({ target, suffix = "", run }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!run) return;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setN(target);
      return;
    }
    let raf;
    const start = performance.now();
    const dur = 1200;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setN(Math.round(p * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, target]);
  return (
    <>
      {n}
      {suffix}
    </>
  );
};

export const StatsStrip = () => {
  const [run, setRun] = useState(false);
  const [stats, setStats] = useState(DEFAULT_STATS);
  const ref = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/settings/stats`)
      .then((r) => {
        if (Array.isArray(r.data.stats) && r.data.stats.length === 4) setStats(r.data.stats);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setRun(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative overflow-hidden bg-stone-900 py-14 sm:py-16" data-testid="home-stats">
      <div className="grain pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
          {stats.map((s, i) => {
            const Icon = ICONS[i] || Heart;
            const numeric = /^\d+$/.test(String(s.value).trim());
            return (
              <div key={s.label} className="text-center" data-testid={`stat-${i}`}>
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-500/15 text-orange-400">
                  <Icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <p className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-white">
                  {numeric ? <CountValue target={parseInt(s.value, 10)} suffix={s.suffix} run={run} /> : `${s.value}${s.suffix || ""}`}
                </p>
                <p className="mt-2 text-base text-stone-300 leading-snug">{s.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
